import React from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon
} from '@radix-ui/react-icons';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable
} from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { parseAsInteger, useQueryState } from 'nuqs';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  totalItems: number;
  onRowClick?: (row: TData) => void;
  emptyState?: React.ReactNode;
  paginationOff?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  totalItems,
  onRowClick,
  emptyState,
  paginationOff = false
}: DataTableProps<TData, TValue>) {
    //const { get, set } = useAppSearchParams();

  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withOptions({ shallow: true })
  );

  const paginationState = {
    pageIndex: currentPage ? currentPage - 1 : 0, // zero-based index for React Table
    pageSize: 20 // Default page size (since limit is not used)
  };
  
  const pageCount = Math.ceil(totalItems / paginationState.pageSize);

  const handlePaginationChange = (
    updaterOrValue:
      | PaginationState
      | ((old: PaginationState) => PaginationState)
  ) => {
    const pagination =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(paginationState)
        : updaterOrValue;
  
    setCurrentPage(pagination.pageIndex + 1);
  };

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination: paginationState },
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualFiltering: true
  });

  // --- same renderPageNumbers as before ---
  const renderPageNumbers = () => {
    const buttons = [];
    const maxButtons = 5;
    const currentPageIndex = table.getState().pagination.pageIndex;
    const totalPages = table.getPageCount();

    let startPage = Math.max(0, currentPageIndex - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(0, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={i === currentPageIndex ? 'default' : 'outline'}
          onClick={() => table.setPageIndex(i)}
          className={`h-8 w-8 p-0 ${
            i === currentPageIndex
              ? 'bg-primary text-white'
              : 'bg-white text-black'
          }`}
        >
          {i + 1}
        </Button>
      );
    }
    return buttons;
  };

  return (
    <div className='space-y-4'>
      <ScrollArea className='grid h-[calc(80vh-220px)] rounded-md border md:h-[calc(90dvh-240px)]'>
        <Table className='relative'>
          <TableHeader className='bg-blue-0 font-grotesk font-medium text-[#0D0C2B]'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className='whitespace-nowrap px-4 py-3 font-bold text-gray-900'
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='text-left font-grotesk text-[#0D0C2B]'
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className='text-black-grey whitespace-nowrap px-4 py-3 text-left'
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div className='flex h-[calc(80vh-220px)] w-full flex-col items-center justify-center gap-3 p-6 text-center'>
                    {emptyState ?? (
                      <p className='text-sm text-muted-foreground'>
                        No data available.
                      </p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation='horizontal' />
      </ScrollArea>

      { !paginationOff && <div className="flex flex-col items-center justify-end gap-2 space-x-2 text-muted-foreground sm:flex-row">
        <div className="flex w-full items-center justify-between">
          <div className="flex-1 text-sm text-muted-foreground">
            {totalItems > 0 ? (
              <>
                Showing {paginationState.pageIndex * paginationState.pageSize + 1} to{' '}
                {Math.min(
                  (paginationState.pageIndex + 1) * paginationState.pageSize,
                  totalItems
                )}{' '}
                of {totalItems} entries
              </>
            ) : (
              'No entries found'
            )}
          </div>
        </div>
        <div className="flex w-full items-center justify-between gap-2 sm:justify-end">
          <div className="flex w-[150px] items-center justify-center text-sm font-medium">
            {totalItems > 0
              ? `Page ${paginationState.pageIndex + 1} of ${table.getPageCount()}`
              : 'No pages'}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              aria-label="Go to first page"
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              aria-label="Go to previous page"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
            {renderPageNumbers()}
            <Button
              aria-label="Go to next page"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              aria-label="Go to last page"
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div> }
    </div>
  );
}
